/**
 * @module results.transformer
 * contain transformer for results tab related results
 */
import { nanoid } from '@reduxjs/toolkit';
import { questionTypeNames } from '~/config/settings/survey_element.settings';

const optionResponseFrom = (result) => {
  return {
    id: nanoid(8),
    rating: result.rating ?? null,
    choiceId: result.id ?? null,
    respondentPercent: result.percentage,
    totalRespondents: result.totalAnswered,
  };
};

const questionResponseFrom = (result) => {
  // counts favorable/neutral/unfavorable only used by text question for now
  const countFavorable = result.aggregatedCommentSentiments?.numberOfPositiveComments ?? null;
  const countUnfavorable = result.aggregatedCommentSentiments?.numberOfNegativeComments ?? null;
  const countUnknown = result.aggregatedCommentSentiments?.numberOfUnknownComments ?? null;
  const countNeutral = result.aggregatedCommentSentiments
    ? result.numberOfComments - countFavorable - countUnfavorable
    : null;

  const optionResults = result.ratings || result.options || [];

  // Note: there was an issue the backend is not calculate totalAnswered correctly for text type questions
  const totalRespondents =
    result.type === questionTypeNames.TEXT ? result.numberOfComments || result.totalAnswered : result.totalAnswered;

  return {
    id: nanoid(8),
    totalRespondents,
    totalRecipients: result.totalRespondents,
    totalComments: result.numberOfComments,
    surveyElement: result.surveyElementId,
    scoreAverage: result.average,
    questionTypeName: result.type,
    countFavorable,
    countNeutral,
    countUnfavorable,
    countUnknown,
    optionResponses: optionResults.map(optionResponseFrom),
  };
};

const questionResponsesFrom = (questionResults) => {
  const questionResponses = questionResults.map(questionResponseFrom);
  return questionResponses;
};

const attributeValueResponseFrom = (attributeValue, count) => {
  const { id, label, anonymized, responseRates } = attributeValue;
  const options =
    !anonymized &&
    responseRates?.count.map((option) => {
      return {
        rating: Number(option.rating),
        percentage: count ? option.value / count : 0, // the backend is sending back percetage based on only answerd, but we want it based on all recipients
        totalAnswered: option.value,
      };
    });

  const optionResponses = options ? options.map(optionResponseFrom) : [];

  const totalRespondents = optionResponses.reduce((count, res) => {
    return count + res.totalRespondents;
  }, 0);

  return {
    id: nanoid(8),
    value: id,
    label: label || id,
    isAnonymized: !!anonymized,
    optionResponses,
    totalRespondents,
    respondentPercent: totalRespondents / count,
  };
};

const attributeResponseFrom = (attribute) => {
  const attributeValueResponses = attribute.values
    ? attribute.values.map((value) => attributeValueResponseFrom(value, attribute.count))
    : [];
  const totalRespondents = attributeValueResponses.reduce((count, res) => {
    return count + res.totalRespondents;
  }, 0);
  return {
    id: nanoid(8),
    employeeAttribute: attribute.id,
    isAnonymized: !!attribute.anonymized,
    attributeValueResponses,
    totalRecipients: attribute.count,
    totalRespondents,
  };
};

const questionAttributeResponseFrom = (questionResponse) => {
  const validAttributes = questionResponse.attributes?.filter((attr) => attr.count > 0);
  const attributeResponses = validAttributes ? validAttributes.map(attributeResponseFrom) : [];
  const surveyElementId = questionResponse.surveyElementId;
  return {
    id: nanoid(8),
    surveyElement: surveyElementId,
    isAnonymized: !!questionResponse.anonymized,
    attributeResponses,
  };
};

const questionAttributeResponsesFrom = (questionResponses) => {
  return questionResponses.map((res) => questionAttributeResponseFrom(res));
};

export { questionResponsesFrom, questionAttributeResponsesFrom };
